import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, ChevronUp, ChevronDown } from 'lucide-react';
import { ui$, toggleCommentColumn } from '../store/ui$';
import { isMobile } from '../constants/breakpoints';
import './CommentControlBar.css';
import { ICON_SIZE } from '../design-system';

/**
 * Comment control bar for article view header
 * Shows: [MessageSquare] Comments N | [↑] | [↓]
 * - Toggle button hides/shows comment gutter (dims to 30% opacity when hidden)
 * - Up/down buttons navigate between comments with looping
 */
const CommentControlBar = () => {
  const { t } = useTranslation('reader');
  const commentColumnVisible = useSelector(() => ui$.commentColumnVisible?.get() ?? true);
  const commentCount = useSelector(() => ui$.articleCommentCount?.get() ?? 0);

  const handleToggleComments = () => {
    // Only allow toggle if there are comments
    if (commentCount === 0) return;

    // Mobile behavior: open slide panel to nearest comment
    if (isMobile()) {
      window.dispatchEvent(new CustomEvent('comment-open-mobile-panel', {
        detail: { findNearest: true }
      }));
    } else {
      toggleCommentColumn();
    }
  };

  const handlePrevComment = () => {
    window.dispatchEvent(new CustomEvent('comment-navigate', { detail: { direction: 'prev' } }));
  };

  const handleNextComment = () => {
    window.dispatchEvent(new CustomEvent('comment-navigate', { detail: { direction: 'next' } }));
  };

  return (
    <div className="comment-control-bar">
      <button
        className={`comment-toggle-btn ${!commentColumnVisible ? 'comments-hidden' : ''} ${commentCount === 0 ? 'no-comments' : ''}`}
        onClick={handleToggleComments}
        title={commentCount === 0 ? t('comments.noComments') : (commentColumnVisible ? t('comments.hideComments') : t('comments.showComments'))}
        disabled={commentCount === 0}
      >
        <MessageSquare size={ICON_SIZE.sm} />
        <span>{commentCount}</span>
      </button>

      {/* Navigation stays available whenever comments exist, even collapsed */}
      {commentCount > 0 && (
        <div className="comment-nav-buttons">
          <button
            className="comment-nav-btn"
            onClick={handlePrevComment}
            title={t('comments.prevComment')}
          >
            <ChevronUp size={ICON_SIZE.sm} />
          </button>
          <button
            className="comment-nav-btn"
            onClick={handleNextComment}
            title={t('comments.nextComment')}
          >
            <ChevronDown size={ICON_SIZE.sm} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CommentControlBar;
